/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: AI 任务相关接口 Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-01-12 17:41:30
 */

import { useCallback, useState } from 'react'
import { message } from 'antd'
import { useAppDispatch } from '../../../../app/hooks'
import {
  createAITask as createAITaskAction,
  queryAITask as queryAITaskAction,
  cancelAITask as cancelAITaskAction
} from '../../../../actions/aiTask'

export interface AITaskPayloadBase {
  userID?: string
}

export interface CreateAITaskPayload extends AITaskPayloadBase {
  aiTaskType: number
  /**
   * 可传入 JSON 字符串或对象，内部会转换为字符串
   * 必须包含 edit_user 字段
   */
  input:
    | string
    | (Record<string, any> & {
        edit_user: string
      })
  userID: string
}

export interface TaskIdPayload extends AITaskPayloadBase {
  taskID: string
}

export interface AITaskData {
  status?: number
  msg?: string
  taskID?: string
  /**
   * 任务状态
   * 1 队列中
   * 2 正在处理
   * 3 处理成功
   * 4 处理失败
   * 5 超时完成
   * 6 任务取消
   */
  taskStatus?: number
  output?: string
}

export interface AITaskResponse {
  code?: number
  msg?: string
  toast_msg?: string
  data?: AITaskData
}

export interface PollAITaskOptions {
  /**
   * 照片数量，用于动态计算超时时间
   * 按照1-100张60s，101-200张120s处理
   * 计算公式：超时时间（秒）= Math.ceil(照片数量 / 100) * 60
   * 轮询间隔：2秒，60秒内执行30次请求
   * 注意：如果传入了 timeout，则不再使用此字段进行动态计算
   */
  photoCount?: number
  /**
   * 超时时间（毫秒）
   * 如果传入此字段，则不再使用 photoCount 进行动态计算
   * 直接使用此值作为超时时间
   */
  timeout?: number
}

const normalizeInput = (input: string | Record<string, any>) =>
  typeof input === 'string' ? input : JSON.stringify(input)

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * 从CDN URL获取内容并解析为JSON
 * @param outputUrl CDN HTTP URL
 * @returns 解析后的JSON对象
 */
export const fetchOutputFromUrl = async <T = any>(
  outputUrl: string
): Promise<T> => {
  try {
    const response = await fetch(outputUrl)
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    const text = await response.text()
    const jsonData = JSON.parse(text)
    return jsonData
  } catch (error) {
    console.error('从URL获取output数据失败:', error)
    throw error
  }
}

type AITaskActionCreator = (payload: Record<string, any>) => any

/**
 * 提供 AI 任务相关的创建 / 查询 / 取消 和 轮询工具
 */
export const useAITaskEvent = () => {
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * 统一分发 action 并处理 loading / 错误提示
   */
  const dispatchRequest = useCallback(
    async (
      actionCreator: AITaskActionCreator,
      payload: Record<string, any>
    ): Promise<AITaskResponse> => {
      setLoading(true)
      setError(null)
      let handled = false
      try {
        const result: AITaskResponse = await dispatch(actionCreator(payload))

        // 根据实际API响应，成功时code为200且data.status为0
        const isError =
          (typeof result?.code === 'number' && result.code !== 200) ||
          (typeof result?.data?.status === 'number' && result.data.status !== 0)
        if (isError) {
          handled = true
          const errMsg =
            result.toast_msg ||
            result.msg ||
            result.data?.msg ||
            'AI任务接口返回异常'
          setError(errMsg)
          message.error(errMsg)
          throw new Error(errMsg)
        }
        return result
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : 'AI任务接口发生异常'
        setError(errMsg)
        if (!handled) {
          message.error(errMsg)
        }
        throw err instanceof Error ? err : new Error(errMsg)
      } finally {
        setLoading(false)
      }
    },
    [dispatch]
  )

  const createAITask = useCallback(
    (payload: CreateAITaskPayload) => {
      const requestBody = {
        ...payload,
        input: normalizeInput(payload.input)
      }
      return dispatchRequest(createAITaskAction, requestBody)
    },
    [dispatchRequest]
  )

  const queryAITask = useCallback(
    (payload: TaskIdPayload) => {
      return dispatchRequest(queryAITaskAction, payload)
    },
    [dispatchRequest]
  )

  const cancelAITask = useCallback(
    (payload: TaskIdPayload) => {
      return dispatchRequest(cancelAITaskAction, payload)
    },
    [dispatchRequest]
  )

  /**
   * 轮询查询任务状态，直到任务结束或超时
   * 当 taskStatus 为 3（处理成功）或 4（处理失败）时停止轮询
   * 超时按照超时完成（状态5）处理
   * 每2秒执行1次查询，60秒内执行30次
   */
  const pollAITaskResult = useCallback(
    async (
      payload: TaskIdPayload,
      options?: PollAITaskOptions
    ): Promise<AITaskResponse> => {
      // 使用固定的轮询间隔：每2秒执行一次查询
      const interval = 2000

      const startTime = Date.now()
      let lastRequestTime = startTime // 上一次请求开始的时间

      // 定义完成状态：3（处理成功）、4（处理失败）
      const completedStatuses = [3, 4]

      while (true) {
        const currentTime = Date.now()
        const elapsed = currentTime - startTime

        try {
          const result = await queryAITask(payload)
          const taskStatus = result.data?.taskStatus

          // 只有任务状态是 3（成功）或 4（失败）时，才停止轮询
          // taskStatus 为 1（待处理）或 2（处理中）时，继续轮询
          if (
            typeof taskStatus === 'number' &&
            completedStatuses.includes(taskStatus)
          ) {
            return result
          }
        } catch (error) {
          // 请求失败时继续轮询，记录错误但不中断
          console.warn('轮询任务状态失败，继续尝试:', error)
        }

        // 计算下一次请求应该开始的时间（距离上一次请求开始时间至少间隔 interval）
        const nextRequestTime = lastRequestTime + interval
        const requestEndTime = Date.now()
        const waitTime = nextRequestTime - requestEndTime

        // 更新上一次请求开始时间
        lastRequestTime = nextRequestTime

        // 等待后继续下一次轮询
        if (waitTime > 0) {
          await sleep(waitTime)
        }
      }
    },
    [queryAITask]
  )

  return {
    loading,
    error,
    createAITask,
    cancelAITask,
    pollAITaskResult
  }
}

export default useAITaskEvent
